/** The only fields this module reads off an ancestry edge (`baseSkillId ⇐ ancestorSkillId`). */
interface AncestryEdge {
  readonly baseSkillId: number;
  readonly ancestorSkillId: number;
}

/** The target node at the head of a path: `getSkillsInfoByLevel` puts it at `path[0][0]`. */
interface OrderableTarget {
  readonly id: number;
  readonly code: string;
}

/**
 * Depth of every target within the **target-only** dependency graph: 0 for a target that requires no
 * other target, otherwise one more than the deepest target it requires.
 *
 * Edges are `baseSkillId ⇐ ancestorSkillId`, so a target's prerequisites are its ancestors.
 * Reachability is transitive and does **not** cut at intermediate targets, so a target that requires
 * another target three links up lands at depth 3.
 *
 * Deliberately not `SkillGraphNode.overallLevel`: that is measured over the whole skill graph from
 * graph roots, which parks every leaf-most target at 0 regardless of the targets it depends on.
 *
 * A target inside a dependency cycle keeps the depth 0 it was seeded with, which keeps this
 * terminating.
 */
export const targetDepths = (
  edges: readonly AncestryEdge[],
  targets: ReadonlySet<number>,
): ReadonlyMap<number, number> => {
  const ancestorsByBase = new Map<number, number[]>();
  edges.forEach(({ baseSkillId, ancestorSkillId }) =>
    ancestorsByBase.set(baseSkillId, [...(ancestorsByBase.get(baseSkillId) ?? []), ancestorSkillId]),
  );

  const requiredTargets = (target: number): number[] => {
    const seen = new Set<number>();
    const stack = [target];
    while (stack.length) {
      for (const ancestorSkillId of ancestorsByBase.get(stack.pop() as number) ?? []) {
        if (seen.has(ancestorSkillId)) continue;
        seen.add(ancestorSkillId);
        stack.push(ancestorSkillId);
      }
    }
    return [...seen].filter(skillId => targets.has(skillId));
  };

  const depths = new Map<number, number>();
  const depthOf = (target: number): number => {
    const cached = depths.get(target);
    if (cached !== undefined) return cached;
    depths.set(target, 0); // cycle guard
    const required = requiredTargets(target);
    depths.set(target, required.length ? Math.max(...required.map(other => depthOf(other) + 1)) : 0);
    return depths.get(target) as number;
  };

  targets.forEach(depthOf);
  return depths;
};

/**
 * Orders a target → level → skill breakdown by each target's dependency depth, so the roadmap
 * follows the prerequisite sequence instead of the order the backend returned ancestry edges in
 * (`getSkillsLevelByUnit` emits `Set` insertion order, which is that response order).
 *
 * Sorts **descending** on purpose: `orderPaths` reverses this outer array at render time, so
 * descending here is what puts the root target at the top of the screen. Ties break on `code`
 * ascending once reversed (the curriculum number on the card), then on `id` for a non-numeric code.
 */
export const orderTargetPaths = <T extends OrderableTarget>(
  paths: T[][][],
  skillAncestries: readonly AncestryEdge[] | null | undefined,
): T[][][] => {
  const targetIds = new Set(paths.map(path => path[0][0].id));
  const depthByTarget = targetDepths(skillAncestries ?? [], targetIds);

  return [...paths].sort((pathA, pathB) => {
    const a = pathA[0][0];
    const b = pathB[0][0];
    return (
      (depthByTarget.get(b.id) ?? 0) - (depthByTarget.get(a.id) ?? 0) || Number(b.code) - Number(a.code) || b.id - a.id
    );
  });
};
